import React, { createContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import {
  getAllTeacherHistories,
  getTeacherHistoryById,
  getTeacherHistoryByTeacherId,
  updateTeacherHistory,
  deleteTeacherHistory
} from '../services/teacherHistoryService';
import Notification from '../components/Common/Notification';

export const TeacherHistoryContext = createContext();

export const TeacherHistoryProvider = ({ children }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [teacherHistories, setTeacherHistories] = useState([]);
  const [selectedTeacherHistory, setSelectedTeacherHistory] = useState(null);
  const [notification, setNotification] = useState({ message: '', isError: false });

  const fetchAuthHeaders = () => {
    const storedUser = localStorage.getItem('user');
    const token = user?.token || (storedUser ? JSON.parse(storedUser).token : '');

    if (!token) {
      console.error('User or token not available');
      return {};
    }

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const showNotification = (message, isError) => {
    setNotification({ message, isError });
  };

  const closeNotification = () => {
    setNotification({ message: '', isError: false });
  };

  const fetchAllTeacherHistories = async () => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getAllTeacherHistories(authHeaders);
      setTeacherHistories(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error fetching teacher histories.';
      showNotification(errorMessage, true);
    }
  };

  const fetchTeacherHistoryById = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getTeacherHistoryById(id, authHeaders);
      setSelectedTeacherHistory(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching teacher history with ID ${id}.`;
      showNotification(errorMessage, true);
    }
  };

  const fetchTeacherHistoryByTeacherId = async (teacherId) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getTeacherHistoryByTeacherId(teacherId, authHeaders);
      setTeacherHistories(data); // Update teacherHistories with the fetched data
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching teacher history for teacher ID ${teacherId}.`;
      showNotification(errorMessage, true);
    }
  };


  const updateExistingTeacherHistory = async (id, teacherHistoryData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const updatedTeacherHistory = await updateTeacherHistory(id, teacherHistoryData, authHeaders);

      setTeacherHistories(prevHistories => {
        // Ensure prevHistories is an array
        if (!Array.isArray(prevHistories)) {
          console.error('Expected prevHistories to be an array but received:', prevHistories);
          return [updatedTeacherHistory];
        }

        return prevHistories.map(history => (history.id === id ? updatedTeacherHistory : history));
      });

      showNotification('Teacher history updated successfully!', false);
      return updatedTeacherHistory;
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error updating teacher history.';
      showNotification(errorMessage, true);
      throw error;
    }
  };


  const removeTeacherHistory = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      await deleteTeacherHistory(id, authHeaders);
      setTeacherHistories(prevHistories => prevHistories.filter(history => history.id !== id));
      showNotification('Teacher history removed successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error removing teacher history.';
      showNotification(errorMessage, true);
    }
  };

  // useEffect(() => {
  //   fetchAllTeacherHistories();
  // }, []);


  useEffect(() => {
    if (!isAuthenticated && !['/', '/login'].includes(window.location.pathname)) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);


  return (
    <TeacherHistoryContext.Provider
      value={{
        teacherHistories,
        selectedTeacherHistory,
        fetchAllTeacherHistories,
        fetchTeacherHistoryById,
        fetchTeacherHistoryByTeacherId,
        updateExistingTeacherHistory,
        removeTeacherHistory,
      }}
    >
      {children}
      {notification.message && (
        <Notification
          message={notification.message}
          isError={notification.isError}
          onClose={closeNotification}
        />
      )}
    </TeacherHistoryContext.Provider>
  );
};
